import type { PricingTier } from "./mock-pricing";

/** Mock event data, ported from the handoff `events-widget.jsx` (`EVENTS`). */
export interface MockEvent {
  id: string;
  title: string;
  type: string;
  instructor: string;
  month: string;
  day: number;
  weekday: string;
  year: number;
  start: string;
  end: string;
  location: string;
  address: string;
  timezone: string;
  price: number;
  /** External seller manages tickets; this amount is display-only. */
  externalTickets?: boolean;
  externalTicketUrl?: string;
  advertisedTicketPrice?: number;
  image: string;
  summary: string;
  /** Long-form copy shown in the detail "About this event" section. */
  description: string;
  /** Real Stripe product id — populated from live data; empty in mock. */
  stripeProductId?: string;
  /** Activity id for post-charge registration (live data; id is the event slug). */
  activityId?: string;
  /** ST1 pre-filter (live data): the event-grouping id this event belongs to —
   * matched against the settings-selected `filterEventIds`. Absent on mock rows
   * (they aren't grouping-backed), so those simply aren't narrowed by it. */
  eventGroupingId?: string;
  /** ST1 pre-filter (live data): the host (provider) user ids on this event —
   * matched against the settings-selected `filterInstructors`. */
  providerUserIds?: number[];
  /** ST1 pre-filter (live data): the location id this event is at — matched
   * against the settings-selected `filterLocations`. */
  locationId?: string;
  /** ISO8601 start — used for chronological sort (live data). */
  startIso?: string;
  /** ISO8601 end — used for TZ-aware time display. */
  endIso?: string;
  openRegistrationTimeFrame?: boolean;
  registrationClosesAt?: string | null;
  /** Entity forms (waiver/intake) attached to this event's activity (live data). */
  forms?: { formId: number; type: string; requirement?: string }[];
  /** PLAT-6994: no drop-in price and not publicly free — the event is only
   * bookable with a membership or class credit, so `price: 0` must not read
   * as "Free". Registration goes through the class booking checkout. */
  requiresEntitlement?: boolean;
  /** Membership / credit options for `requiresEntitlement` events (live data). */
  tiers?: PricingTier[];
  /** Program identity when this card represents a multi-session program. */
  program?: {
    programKey: string;
    sessionKeys: string[];
    sessions: Array<{
      sessionKey: string;
      label: string;
      startsAt: string;
      endsAt: string;
      providerName?: string;
      locationName?: string;
    }>;
  };
}

export function getSafeExternalTicketUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") &&
      url.hostname &&
      !url.username &&
      !url.password
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

export const MOCK_EVENTS: MockEvent[] = [
  {
    id: 'community-pilates',
    title: 'Community Pilates for Women',
    type: 'Community class',
    instructor: 'Marisol Pena',
    month: 'May', day: 1, weekday: 'Friday', year: 2026,
    start: '11:00 AM', end: '1:00 PM',
    location: 'Halcyon Studio',
    address: '218 Atlantic Ave, Brooklyn, NY 11201',
    timezone: 'America/Chicago',
    price: 25,
    image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1200&q=70',
    summary:
      'A welcoming community class for women of all levels — mindful movement and core-strengthening exercises with guided modifications throughout.',
    description:
      'Join us for an uplifting community Pilates class designed for women of all fitness levels, blending mindful movement with core-strengthening exercises. Expect a welcoming, supportive vibe with guided modifications so you can move confidently at your own pace. Come connect, sweat, and leave feeling stronger, more aligned, and refreshed.',
  },
  {
    id: 'spring-workshop',
    title: 'Spring Reformer Workshop',
    type: 'Workshop',
    instructor: 'Lena Ortiz',
    month: 'May', day: 30, weekday: 'Saturday', year: 2026,
    start: '1:00 PM', end: '4:00 PM',
    location: 'Reformer Room',
    address: '218 Atlantic Ave, Brooklyn, NY 11201',
    timezone: 'America/New_York',
    price: 65,
    image: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&w=1200&q=70',
    summary:
      'A three-hour deep dive built for intermediate practitioners. Form and breath, sequencing and progressions, then a long guided flow.',
    description:
      'A three-hour intensive built for practitioners with at least six months on the reformer. We will spend the first hour on form and breathwork, the second on sequencing and progressions, and the third on a long, guided flow. Bring water and a small towel.',
  },
  {
    id: 'breathwork-evening',
    title: 'Breathwork & Restorative Evening',
    type: 'Workshop',
    instructor: 'Anika Rao',
    month: 'Jun', day: 8, weekday: 'Monday', year: 2026,
    start: '6:30 PM', end: '8:00 PM',
    location: 'Studio A',
    address: '218 Atlantic Ave, Brooklyn, NY 11201',
    timezone: 'America/New_York',
    price: 35,
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=70',
    summary:
      'A slow, intentional evening to reset the nervous system — guided breath practices, long-held restorative shapes, and a closing meditation.',
    description:
      'A slow, intentional evening designed to reset the nervous system. We will move through a sequence of guided breath practices, then settle into long-held restorative shapes supported by props. Closing with a brief meditation and tea. Bring a journal if you would like.',
  },
  {
    id: 'open-house',
    title: 'Summer Open House',
    type: 'Open house',
    instructor: 'Studio Team',
    month: 'Jun', day: 22, weekday: 'Monday', year: 2026,
    start: '5:00 PM', end: '8:00 PM',
    location: 'Halcyon Studio',
    address: '218 Atlantic Ave, Brooklyn, NY 11201',
    timezone: 'America/New_York',
    price: 0,
    image: 'https://images.unsplash.com/photo-1532384748853-8f54a8f476e2?auto=format&fit=crop&w=1200&q=70',
    summary:
      'Free and open to the public. Tour the studio, meet our instructors, sample mini-classes, and grab a discounted intro pack only available on the night.',
    description:
      'Free, open to the public. Drop by anytime between 5 and 8. Tour the studio, meet our instructors, sample 15-minute mini-classes on the reformer, and grab a discounted intro pack only available on the night. Light refreshments served.',
  },
];
